/**
 * 维度值数据 Hook — 板块 E 国家分析
 *
 * 调用 dimension_values 接口获取指定 metric 的维度值列表
 * 返回 items（国家代码 + value）和数据日期
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getAdCreativeDimensionValues } from '@op/services/adCreative';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

export interface IDimensionItem {
  dimension_value: string;
  value: number;
}

export interface IUseDimensionDataOptions {
  filter?: {
    start_time: string;
    end_time: string;
  };
  gameInfo?: {
    edition_unified_id?: string;
    entity_type?: string;
    id_type?: string;
  };
  metric: string;
  dimensionValues?: string[];
  topN?: number;
  enabled?: boolean;
}

export interface IUseDimensionDataResult {
  items: IDimensionItem[];
  dataDate: string;
  loading: boolean;
  isEmpty: boolean;
}

export const useDimensionData = (
  options: IUseDimensionDataOptions,
): IUseDimensionDataResult => {
  const {
    filter,
    gameInfo,
    metric,
    dimensionValues,
    topN = 10,
    enabled = true,
  } = options;

  const [data, setData] = useState<any>(null);

  const { loading, wrapperCancelRequest } = useCancelRequestHook<any>({
    clear: () => {
      setData(null);
    },
  });

  const fetchData = useCallback(async () => {
    if (
      !filter?.start_time ||
      !filter?.end_time ||
      !gameInfo?.edition_unified_id ||
      !enabled
    ) {
      return;
    }

    try {
      const params: any = {
        edition_unified_id: gameInfo.edition_unified_id,
        entity_type: gameInfo.entity_type,
        id_type: gameInfo.id_type,
        metric,
        top_n: topN,
        ...filter,
      };

      if (dimensionValues?.length) {
        params.dimension_values = dimensionValues;
      }

      const res = await wrapperCancelRequest(
        getAdCreativeDimensionValues,
        params,
      );
      setData(res);
    } catch (err) {
      console.error('获取维度数据失败:', err);
    }
  }, [
    filter,
    gameInfo,
    metric,
    dimensionValues,
    topN,
    enabled,
    wrapperCancelRequest,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const result = useMemo(() => {
    if (!data?.items?.length) {
      return {
        items: [],
        dataDate: '',
        isEmpty: true,
      };
    }

    return {
      items: data.items as IDimensionItem[],
      dataDate: data.data_date || '',
      isEmpty: false,
    };
  }, [data]);

  return { ...result, loading };
};

export default useDimensionData;
